<script lang="ts">
  import { getShopifyImageUrl, getFirstImage, slugify, formatTitle, type ProductNode } from '$lib/utils/shopify';
  import { m } from "$lib/paraglide/messages";
  import { localizeHref } from '$lib/paraglide/runtime';
  import ScrollTracker from '$lib/components/ScrollTracker.svelte';
  import type { PageData } from './$types';

  let { data }: { data: PageData } = $props();

  let products = $derived(data.products.map(p => ({
    ...p,
    slug: data.slugMapping?.[p.handle] ?? p.slug ?? slugify(p.title),
    formattedTitle: data.titleMapping?.[p.handle] ?? p.formattedTitle ?? formatTitle(p.title)
  })));
</script>

<svelte:head>
  <title>kybun Mats | Healthy Shoes Cyprus</title>
  <meta name="description" content="Shop premium kybun mats in Cyprus. Experience comfort and wellness with our collection of ergonomic standing mats." />

  <!-- Open Graph / Facebook -->
  <meta property="og:type" content="website" />
  <meta property="og:title" content="kybun Mats | Healthy Shoes Cyprus" />
  <meta property="og:description" content="Shop premium kybun mats in Cyprus. Experience comfort and wellness with our collection of ergonomic standing mats." />
  {#if products.length > 0}
    {@const firstImage = getFirstImage(products[0])}
    {#if firstImage}
      <meta property="og:image" content={getShopifyImageUrl(firstImage.url, 1200, 'jpg')} />
    {/if}
  {/if}

  <!-- Twitter Card -->
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="kybun Mats | Healthy Shoes Cyprus" />
  <meta name="twitter:description" content="Shop premium kybun mats in Cyprus. Experience comfort and wellness with our collection of ergonomic standing mats." />
  {#if products.length > 0}
    {@const firstImage = getFirstImage(products[0])}
    {#if firstImage}
      <meta name="twitter:image" content={getShopifyImageUrl(firstImage.url, 1200, 'jpg')} />
    {/if}
  {/if}

  <!-- Preload the first product image for LCP optimization -->
  {#if products.length > 0}
    {@const firstProduct = products[0]}
    {@const firstImage = getFirstImage(firstProduct)}
    {#if firstImage}
      <link
        rel="preload"
        as="image"
        href={getShopifyImageUrl(firstImage.url, 400, 'webp')}
        fetchpriority="high"
      />
    {/if}
  {/if}
</svelte:head>

<div class="max-w-container mx-auto px-0 desktop:px-12">
  <!-- Title bar (desktop only) -->
  <div class="hidden desktop:flex justify-between items-center py-6 border-b border-surface-200 dark:border-surface-700">
    <div>
      <h1 class="text-3xl font-medium">
        {m.mats()}
      </h1>
      <p class="text-surface-500 dark:text-surface-400 text-sm mt-1">
        {m.mats_subtitle()}
      </p>
    </div>
  </div>

  <!-- Product Grid -->
  <main class="pt-3 desktop:pt-6">
    {#if products.length === 0}
      <p class="text-center py-20 text-surface-500">No products found.</p>
    {:else}
      <div class="flex flex-wrap">
        {#each products as product, index (product.id)}
          <div class="w-1/2 desktop:w-1/3 px-[3px] pb-6 desktop:px-2 desktop:pb-10">
            <ScrollTracker
              startThreshold={0}
              endThreshold={0.32}
              debug={false}
            >
              {#snippet children(progress)}
                {@const firstImage = getFirstImage(product)}
                {@const variant = product.variants.edges[0]?.node}
                <div
                  class="opacity-[var(--scroll-progress)]
                    [transform:translateY(calc(30px*(1-var(--scroll-progress))))]
                    will-change-[opacity,transform]
                    transition-[opacity,transform] duration-200 ease-out"
                >
                  <a href={localizeHref(`/mats/${product.slug}`)} class="block group">
                    <!-- Image -->
                    <div class="relative aspect-square overflow-hidden rounded-lg bg-surface-100 dark:bg-surface-800 mb-3">
                      {#if firstImage}
                        <img
                          src={getShopifyImageUrl(firstImage.url, 400, 'webp')}
                          alt={firstImage.altText || product.title}
                          class="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                          loading={index < 6 ? 'eager' : 'lazy'}
                        />
                      {:else}
                        <div class="w-full h-full flex items-center justify-center text-surface-400">
                          No image
                        </div>
                      {/if}
                    </div>

                    <!-- Product info -->
                    <h2 class="font-medium text-sm desktop:text-base group-hover:text-primary-600 transition-colors">
                      {product.formattedTitle}
                    </h2>

                    <!-- Price -->
                    {#if variant}
                      <p class="text-surface-600 dark:text-surface-400 text-sm mt-1">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: variant.price.currencyCode,
                        }).format(parseFloat(variant.price.amount))}
                      </p>
                    {/if}
                  </a>
                </div>
              {/snippet}
            </ScrollTracker>
          </div>
        {/each}
      </div>
    {/if}
  </main>
</div>
